import { forwardRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { TagManagement } from "../components/TagManagement";
import { FormField } from '../components/FormField';

interface Prompt {
  promptId: string;
  promptName: string;
  promptInformation: string;
  promptTags: string[];
}

// View Mode Field Component
const ViewModeField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="mb-6">
    <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
    {typeof value === 'string' ? (
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200">
        {value || 'No content provided'}
      </div>
    ) : (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {value}
      </div>
    )}
  </div>
);

interface PromptFormProps {
  prompt: Prompt;
  originalPrompt?: Prompt | null;
  onPromptChange: (prompt: Prompt) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly: (value: boolean) => void;
  tagSuggestions?: string[];
}

export interface PromptFormRef {
  validateForm: () => boolean;
  getErrors: () => Record<string, string>;
}

const PromptForm = forwardRef<PromptFormRef, PromptFormProps>((props, ref) => {
  const {
    prompt,
    onPromptChange,
    onSubmit,
    onCancel,
    isEditing = false,
    readOnly = false,
    onEditClick,
    onDiscardChanges,
    hasUnsavedChanges = false,
    tagSuggestions = []
  } = props;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    validateForm: () => {
      const newErrors: Record<string, string> = {};

      if (!prompt.promptName.trim()) {
        newErrors.promptName = 'Prompt Name is required';
      }
      
      if (!prompt.promptInformation.trim()) {
        newErrors.promptInformation = 'Prompt Information is required';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    getErrors: () => errors
  }), [prompt, errors]);

  const handleInputChange = (field: keyof Prompt, value: any) => {
    if (readOnly) return;
    
    onPromptChange({
      ...prompt,
      [field]: value
    });
  };

  const handleBackToAdmin = () => {
    if (hasUnsavedChanges && !readOnly) {
      // If there are unsaved changes, trigger the warning dialog
      onCancel();
    } else {
      // If no unsaved changes, navigate directly
      navigate('/admin');
    }
  };

  const renderEditMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-2xl">
          {isEditing ? 'Edit Prompt' : 'Add New Prompt'}
        </CardTitle>
        <Button
          variant="outline"
          onClick={handleBackToAdmin}
          className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Admin
        </Button>
      </CardHeader>

      <CardContent className="space-y-8">
        <FormField
          label="Prompt Name"
          helpText="Enter a descriptive name for your prompt"
          required
          error={errors.promptName}
        >
          <Input
            value={prompt.promptName}
            onChange={(e) => handleInputChange('promptName', e.target.value)}
            placeholder="Enter prompt name"
            className={errors.promptName ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Prompt Information"
          helpText="Provide detailed information about the prompt"
          required
          error={errors.promptInformation}
        >
          <Textarea
            value={prompt.promptInformation}
            onChange={(e) => handleInputChange('promptInformation', e.target.value)}
            placeholder="Enter prompt information"
            rows={5}
            className={errors.promptInformation ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Prompt Tags"
          helpText="Add tags to help categorize and find your prompt"
        >
          <TagManagement
            tags={prompt.promptTags}
            onChange={(newTags) => handleInputChange('promptTags', newTags)}
            readOnly={false}
            suggestions={tagSuggestions}
          />
        </FormField>

        <div className="flex justify-end gap-2 pt-4">
          {isEditing && onDiscardChanges && (
            <Button
              variant="outline"
              onClick={onDiscardChanges}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel Edit
            </Button>
          )}
          <Button
            variant="default"
            onClick={onSubmit}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderViewMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-2xl">{prompt.promptName}</CardTitle>
        <div className="flex gap-2">
          {onEditClick && (
            <Button
              variant="outline"
              onClick={onEditClick}
              className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleBackToAdmin}
            className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-8">
        <ViewModeField 
          label="Information" 
          value={prompt.promptInformation} 
        />
        
        <ViewModeField 
          label="Tags" 
          value={
            <TagManagement
              tags={prompt.promptTags}
              onChange={() => {}}
              readOnly={true}
              suggestions={tagSuggestions}
            />
          } 
        />
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4" data-has-unsaved-changes={hasUnsavedChanges}>
      {readOnly ? renderViewMode() : renderEditMode()}
    </div>
  );
});

export default PromptForm;